// /app/frontend/src/components/frugal-v2/components/DashboardHeader.jsx
// Header with logo, alerts icon, and user menu trigger (< 200 lines)

import React, { useState, useEffect, useRef } from 'react';
import { User, ChevronDown, Bell, Mail } from 'lucide-react';
import UserMenu from './UserMenu';
import BankEmailModal from './BankEmailModal';
import MonthSummaryModal from './MonthSummaryModal';
import MonthEmailsModal from './MonthEmailsModal';
import ScheduledChargesModal from './ScheduledChargesModal';
import ZenLockConfigModal from './ZenLockConfigModal';
import CurtainAdminModal from './CurtainAdminModal';
import ManageSubscriptionModal from './ManageSubscriptionModal';
import CashflowReportModal from './CashflowReportModal';
import ReportsHistoryModal from './ReportsHistoryModal';
import AlertsDropdown from './AlertsDropdown';
import { useVIPSupport } from '../hooks/useVIPSupport';
import { useAuth } from '../hooks/useAuth';

const DashboardHeader = ({ 
  user, onLogout, onLanguageChange, onCloseMonth, currentLanguage,
  alerts, pendingEmails, onOpenEmails, zenLockConfig, onUpdateZenLock, t 
}) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showAlerts, setShowAlerts] = useState(false);
  const [showBankEmailModal, setShowBankEmailModal] = useState(false);
  const [showMonthSummary, setShowMonthSummary] = useState(false);
  const [showMonthEmails, setShowMonthEmails] = useState(false);
  const [showScheduledCharges, setShowScheduledCharges] = useState(false);
  const [showZenLockConfig, setShowZenLockConfig] = useState(false);
  const [showCurtainAdmin, setShowCurtainAdmin] = useState(false);
  const [showManageSubscription, setShowManageSubscription] = useState(false);
  const [showCashflow, setShowCashflow] = useState(false);
  const [showReportsHistory, setShowReportsHistory] = useState(false);
  const alertsRef = useRef(null);
  const bellRef = useRef(null);
  const { openWhatsApp } = useVIPSupport();
  const { token } = useAuth();

  const totalAlerts = alerts?.length || 0;

  // Click outside to close alerts
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showAlerts && alertsRef.current && !alertsRef.current.contains(e.target) && 
          bellRef.current && !bellRef.current.contains(e.target)) {
        setShowAlerts(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAlerts]);

  // Auto-close alerts after 5s
  useEffect(() => {
    if (showAlerts && totalAlerts > 0) {
      const timer = setTimeout(() => setShowAlerts(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [showAlerts, totalAlerts]);

  return (
    <header className="sticky top-0 z-40 bg-slate-900/80 backdrop-blur-sm border-b border-white/10">
      <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-2">
          <img src="/zensaver-logo.png" alt="ZenSaver" className="w-10 h-10 rounded-lg object-contain" />
          <span className="text-white font-semibold">ZenSaver</span>
        </div>

        <div className="flex items-center gap-2">
          {/* Email Icon */}
          <button
            onClick={onOpenEmails}
            className={`relative p-2 rounded-full transition-colors ${
              pendingEmails > 0 ? 'bg-sky-500/20 hover:bg-sky-500/30' : 'bg-white/10 hover:bg-white/20'
            }`}
          >
            <Mail className={`w-5 h-5 ${pendingEmails > 0 ? 'text-sky-400' : 'text-slate-400'}`} />
            {pendingEmails > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                {pendingEmails > 9 ? '9+' : pendingEmails}
              </span>
            )}
          </button>

          {/* Alert Bell */}
          <div className="relative" ref={bellRef}>
            <button
              onClick={() => setShowAlerts(!showAlerts)}
              className={`relative p-2 rounded-full transition-colors ${
                totalAlerts > 0 ? 'bg-amber-500/20 hover:bg-amber-500/30' : 'bg-white/10 hover:bg-white/20'
              }`}
            >
              <Bell className={`w-5 h-5 ${totalAlerts > 0 ? 'text-amber-400' : 'text-slate-400'}`} />
              {totalAlerts > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                  {totalAlerts > 9 ? '9+' : totalAlerts}
                </span>
              )}
            </button>
            {showAlerts && (
              <AlertsDropdown
                ref={alertsRef}
                alerts={alerts}
                onClose={() => setShowAlerts(false)}
                t={t}
              />
            )}
          </div>

          {/* User Menu Trigger */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
            >
              <User className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-white">{user?.name?.split(' ')[0] || 'Frugal'}</span>
              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
            </button>

            <UserMenu
              user={user}
              isOpen={showUserMenu}
              onClose={() => setShowUserMenu(false)}
              onLogout={onLogout}
              onLanguageChange={onLanguageChange}
              onCloseMonth={() => { setShowUserMenu(false); onCloseMonth(); }}
              onOpenBankEmail={() => { setShowUserMenu(false); setShowBankEmailModal(true); }}
              onOpenSubscription={() => { setShowUserMenu(false); setShowManageSubscription(true); }}
              onOpenMonthSummary={() => { setShowUserMenu(false); setShowMonthSummary(true); }}
              onOpenCashflow={() => { setShowUserMenu(false); setShowCashflow(true); }}
              onOpenMonthEmails={() => { setShowUserMenu(false); setShowMonthEmails(true); }}
              onOpenScheduledCharges={() => { setShowUserMenu(false); setShowScheduledCharges(true); }}
              onOpenSupport={() => { setShowUserMenu(false); openWhatsApp(); }}
              onOpenZenLock={() => { setShowUserMenu(false); setShowZenLockConfig(true); }}
              onOpenCurtainAdmin={() => { setShowUserMenu(false); setShowCurtainAdmin(true); }}
              onOpenReportsHistory={() => { setShowUserMenu(false); setShowReportsHistory(true); }}
              currentLanguage={currentLanguage}
              t={t}
            />
          </div>
        </div>
      </div>

      {/* Modals */}
      <BankEmailModal isOpen={showBankEmailModal} onClose={() => setShowBankEmailModal(false)} t={t} />
      <MonthSummaryModal isOpen={showMonthSummary} onClose={() => setShowMonthSummary(false)} />
      <MonthEmailsModal isOpen={showMonthEmails} onClose={() => setShowMonthEmails(false)} token={token} t={t} />
      <ScheduledChargesModal isOpen={showScheduledCharges} onClose={() => setShowScheduledCharges(false)} />
      <ZenLockConfigModal
        isOpen={showZenLockConfig}
        onClose={() => setShowZenLockConfig(false)}
        config={zenLockConfig}
        onUpdate={onUpdateZenLock}
        t={t}
      />
      <CurtainAdminModal
        isOpen={showCurtainAdmin}
        onClose={() => setShowCurtainAdmin(false)}
        token={token}
        t={t}
      />
      <ManageSubscriptionModal
        isOpen={showManageSubscription}
        onClose={() => setShowManageSubscription(false)}
        token={token}
      />
      <CashflowReportModal
        isOpen={showCashflow}
        onClose={() => setShowCashflow(false)}
      />
      <ReportsHistoryModal
        isOpen={showReportsHistory}
        onClose={() => setShowReportsHistory(false)}
        t={t}
        currentLanguage={currentLanguage}
      />
    </header>
  );
};

export default DashboardHeader;
